import useSWR from 'swr';

import { Currency, findLocaleFromUrlAndCookie } from '@zooplus/commons-lang';

export type MembershipStatus =
  | 'ACTIVE'
  | 'FUTURE'
  | 'EXPIRED'
  | 'CANCELLED'
  | 'DELETED'
  | 'PENDING';

export type MembershipExpirationStatus = 'BEFORE_EXPIRE' | 'AFTER_EXPIRE' | 'NO_EXPIRE';

export type LevelType = 'BASIC' | 'PREMIUM' | 'SP_MIGRATION';

export type MembershipOfferBenefitType =
  | {
      type: 'PERCENTAGE_DISCOUNT';
      discount: number;
    }
  | {
      type: 'BONUS_POINTS_WELCOME_GIFT';
      amount: number;
    }
  | {
      type: 'EXCLUSIVE_OFFERS';
    }
  | {
      type: 'NO_AUTOMATIC_RENEWAL';
      count: number;
    };

export const membershipOfferBenefitTypes: Array<MembershipOfferBenefitType['type']> = [
  'PERCENTAGE_DISCOUNT',
  'BONUS_POINTS_WELCOME_GIFT',
  'EXCLUSIVE_OFFERS',
  'NO_AUTOMATIC_RENEWAL',
];

export type MembershipOffer = {
  levelKey: string;
  articleId: number;
  price: MembershipOfferPrice;
  referencePrice?: MembershipOfferPrice;
  secondaryCurrencyPrice?: MembershipOfferPrice;
  benefits: Array<MembershipOfferBenefitType>;
  durationInDays?: number;
};

export interface MembershipOfferPrice {
  amount: number;
  currency: Currency;
}

export interface MembershipOverview {
  levelKey: string;
  startDate: Date;
  endDate?: Date;
  status: MembershipStatus;
  type: LevelType;
}

export interface MembershipOfferInfo {
  renewInfo?: MembershipOverview;
  offer: MembershipOffer;
}

export function useMembershipOffer(shouldFetch: boolean = true) {
  return useSWR(
    shouldFetch ? '/protected/api/loyalty-management/memberships/offer' : null,
    async (...args): Promise<MembershipOfferInfo> => {
      const response = await fetch(...args, {
        headers: { locale: findLocaleFromUrlAndCookie() || 'en-GB' },
      });

      return response.status === 200 ? await response.json() : undefined;
    },
    {
      errorRetryInterval: 1000,
      errorRetryCount: 3,
    },
  );
}

export function useMembershipExpirationStatus(renewInfo?: MembershipOverview) {
  const endDate = new Date(renewInfo?.endDate ? renewInfo.endDate : '');

  const getExpirationStatus = (): MembershipExpirationStatus => {
    if (!renewInfo) return 'NO_EXPIRE';
    if (renewInfo.status === 'EXPIRED') return 'AFTER_EXPIRE';
    if (renewInfo.status === 'ACTIVE' && endDate) {
      const msUntilEnd = new Date(endDate).getTime() - Date.now();
      if (msUntilEnd < 42 * 24 * 60 * 60 * 1000) return 'BEFORE_EXPIRE';
    }
    return 'NO_EXPIRE';
  };

  return { getExpirationStatus, endDate };
}
